import { createSelector, createSlice, type Draft, type PayloadAction } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';
import { formatISO } from 'date-fns';
import { v4 } from 'uuid';

import { mifidApi } from '@/api/mifid/index.ts';
import { getWorkspaceMatchFulfilled } from '@/api/workspace/index.ts';
import { mapToForwardAccumulator } from '@/domain/FxForwardAccumulator/FxForwardAccumulator.utils.ts';
import { forwardAccumulatorApi } from '@/store/forwardAccumulator/api/index.ts';
import { createForwardAccumulatorQuote } from '@/store/forwardAccumulator/api/multiPass/createForwardAccumulatorQuote';
import type { AccumulatorReplyMessage } from '@/store/forwardAccumulator/api/multiPass/requestForwardAccumulator.model.ts';
import { getForwardAccumulatorRfsId } from '@/store/forwardAccumulator/selectors.ts';
import type {
  DisplayMode,
  ForwardAccumulatorById,
  ForwardAccumulatorRfs,
  FxForwardAccumulator,
} from '@/store/forwardAccumulator/state.ts';
import { initForwardAccumulator } from '@/store/forwardAccumulator/utils.ts';
import { reduceForwardAccumulatorOnFetchSharedWorkspace } from '@/store/sharedWorkspace/workflows/getSharedTab.workflow.ts';
import { reduceForwardAccumulatorStateOnSharedTabRemoved } from '@/store/sharedWorkspace/workflows/onSharedTabRemoved.workflow.ts';
import { addTile, duplicateTile, removeTile, updateTileProductType } from '@/store/tiles/slice.ts';
import type { CurrencyPair } from '@/types/currency.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import {
  type IHeartbeat,
  type IQuoteAbort,
  type IQuoteTraderNotification,
  isQuoteWithdraw,
  type QuoteWithPrice,
} from '@/utils/rfs/type.ts';

const createForwardAccumulatorSelector = createSelector.withTypes<ForwardAccumulatorById>();

export const forwardAccumulatorSlice = createSlice({
  name: 'forwardAccumulator',
  reducerPath: 'forwardAccumulator',
  initialState: {} as ForwardAccumulatorById,
  reducers: {
    forwardAccumulatorClientIdUpdated: (
      state,
      action: PayloadAction<
        | { tileId: string; clientId: number | undefined; isTabClientOverriden: true }
        | { tileId: string; clientId: number; isTabClientOverriden: false }
      >,
    ) => {
      const tile = state[action.payload.tileId];
      if (isNotDefined(tile) || tile.clientId === action.payload.clientId) {
        return;
      }
      if (action.payload.isTabClientOverriden === true) {
        tile.clientId = action.payload.clientId;
      }

      tile.properties.tiering = undefined;
    },
    setForwardAccumulatorMaxDate(state, action: PayloadAction<{ tileId: string; maxDate: string | undefined }>) {
      const { tileId, maxDate } = action.payload;
      withForwardAccumulator(tileId, state, (forwardAccumulator) => {
        forwardAccumulator.properties.maxDate = maxDate;
      });
    },
    // not observed in a listener, why bypassing trade capture roundtrip here ?
    notionalCurrencyChanged: (
      state,
      {
        payload: { tileId, notionalCurrencyIndex },
      }: PayloadAction<{ tileId: string; notionalCurrencyIndex: CurrencyIndex }>,
    ) => {
      if (isNotDefined(state[tileId]?.properties.notionalCurrencyIndex)) {
        return state;
      }
      state[tileId].properties.notionalCurrencyIndex = notionalCurrencyIndex;
    },
    forwardAccumulatorReset: (state, action: PayloadAction<{ tileId: string; forcedCurrencyPair?: CurrencyPair }>) => {
      const { tileId, forcedCurrencyPair } = action.payload;
      withForwardAccumulator(tileId, state, (accumulator) => {
        state[tileId] = initForwardAccumulator(
          accumulator.clientId,
          forcedCurrencyPair ?? accumulator.properties.currencyPair,
        );
      });
    },
    forwardAccumulatorTieringUpdated(state, action: PayloadAction<{ tileId: string; tiering: string }>) {
      const tile = state[action.payload.tileId];
      if (isNotDefined(tile)) {
        return;
      }
      tile.properties.tiering = action.payload.tiering;
    },
    forwardAccumulatorCrossCurrencyUpdated(state, action: PayloadAction<{ tileId: string; crossCurrency: string }>) {
      const tile = state[action.payload.tileId];
      if (isNotDefined(tile)) {
        return;
      }
      tile.properties.crossCurrency = action.payload.crossCurrency;
    },
    forwardAccumulatorErrorOccurred: (state, action: PayloadAction<{ tileId: string }>) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile)) {
        return;
      }

      tile.tradeCapture = {
        error: 'TIMEOUT',
        status: 'ERROR',
        sessionId: tile.tradeCapture.sessionId,
        version: tile.tradeCapture.version,
      };
    },
    changeClient(state, action: PayloadAction<{ tileId: string; clientId: number | undefined }>) {
      const { tileId, clientId } = action.payload;
      withForwardAccumulator(tileId, state, (tile) => {
        tile.clientId = clientId;
      });
    },
    forwardAccumulatorRfsHeartbeat(_state, _action: PayloadAction<{ tileId: string; heartbeat: IHeartbeat }>) {},
    forwardAccumulatorRfsQuoteAborted(state, action: PayloadAction<{ tileId: string; reply: IQuoteAbort }>) {
      const { tileId, reply } = action.payload;
      withForwardAccumulator(tileId, state, (accumulator) => {
        if (reply.QuoteReject.ErrorCode === 'EM_TIMEOUT') {
          accumulator.properties.rfs = makeExpired(accumulator.properties.rfs);
        } else {
          accumulator.properties.rfs = {
            status: 'ERROR',
            rfsId: getForwardAccumulatorRfsId(accumulator),
            error: {
              multipassErrorCode: reply.QuoteReject.MultipassErrorCode,
              errorCode: reply.QuoteReject.ErrorCode,
              errorMessage: reply.QuoteReject.ErrorMessage,
            },
          };
        }
      });
    },
    forwardAccumulatorRfsQuoteReceived(
      state,
      action: PayloadAction<{ tileId: string; message: QuoteWithPrice<AccumulatorReplyMessage> }>,
    ) {
      const { tileId, message } = action.payload;
      withForwardAccumulator(tileId, state, (accumulator) => {
        const quote = createForwardAccumulatorQuote(message);
        if (accumulator.properties.rfs?.status === 'PRICING' || accumulator.properties.rfs?.status === 'AWAITING') {
          const initialRfsWindow =
            accumulator.properties.rfs.status === 'PRICING'
              ? accumulator.properties.rfs.initialRfsWindow
              : quote.rfsWindow;
          accumulator.properties.rfs = {
            status: 'PRICING',
            quote,
            rfsId: accumulator.properties.rfs.rfsId,
            quoteType: isQuoteWithdraw(message) ? 'Indicative' : quote.quoteType,
            quoteId: quote.quoteId,
            traderId: accumulator.properties.rfs.traderId,
            initialRfsWindow,
          };
        }
      });
    },
    forwardAccumulatorTraderNotificationReceived: (
      state,
      action: PayloadAction<{ tileId: string; message: IQuoteTraderNotification<AccumulatorReplyMessage> }>,
    ) => {
      withForwardAccumulator(action.payload.tileId, state, (accumulator) => {
        const traderId = action.payload.message.AccumulatorQuoteReply.TraderId ?? undefined;
        if (accumulator.properties.rfs.status === 'PRICING' || accumulator.properties.rfs.status === 'AWAITING') {
          accumulator.properties.rfs = {
            ...accumulator.properties.rfs,
            traderId,
          };
        }
      });
    },
    forwardAccumulatorPending: (state, action: PayloadAction<{ tileId: string; executionId: string }>) => {
      const { tileId, executionId } = action.payload;
      withForwardAccumulator(tileId, state, (forwardAccumulator) => {
        forwardAccumulator.execution = {
          status: 'PENDING',
          executionId,
        };
        if (isDefined(forwardAccumulator.properties.rfs)) {
          forwardAccumulator.properties.rfs = makeExpired(forwardAccumulator.properties.rfs);
        }
      });
    },
    forwardAccumulatorExecutionSucceed: (state, action: PayloadAction<{ tileId: string }>) => {
      withForwardAccumulator(action.payload.tileId, state, (forwardAccumulator) => {
        if (isNotDefined(forwardAccumulator.execution)) {
          return;
        }
        forwardAccumulator.execution.status = 'SUCCESS';
      });
    },
    forwardAccumulatorExecutionRejected: (state, action: PayloadAction<{ tileId: string }>) => {
      withForwardAccumulator(action.payload.tileId, state, (forwardAccumulator) => {
        if (isNotDefined(forwardAccumulator.execution)) {
          return;
        }
        forwardAccumulator.execution.status = 'REJECTED';
      });
    },
    forwardAccumulatorRejectedExecutionReset: (state, action: PayloadAction<{ tileId: string }>) => {
      withForwardAccumulator(action.payload.tileId, state, (forwardAccumulator) => {
        if (isNotDefined(forwardAccumulator.execution)) {
          return;
        }
        forwardAccumulator.execution = undefined;
      });
    },
    forwardAccumulatorExecutionReset: (state, action: PayloadAction<{ tileId: string }>) => {
      const { tileId } = action.payload;
      withForwardAccumulator(tileId, state, (forwardAccumulator) => {
        forwardAccumulator.execution = undefined;
      });
    },

    forwardAccumulatorSetDisplayMode: (state, action: PayloadAction<{ tileId: string; displayMode: DisplayMode }>) => {
      const { tileId, displayMode } = action.payload;

      withForwardAccumulator(tileId, state, (accumulator) => {
        accumulator.properties.uiState.displayMode = displayMode;
      });
    },
  },

  extraReducers: (builder) => {
    reduceForwardAccumulatorStateOnSharedTabRemoved(builder);

    builder.addCase(updateTileProductType, (state, { payload: { tileId, productType, currencyPair } }) => {
      if (productType !== 'ForwardAccumulator') {
        if (isDefined(state[tileId])) {
          // changed from ForwardAccumulator to another product type
          // we have to clean out state here
          delete state[tileId];
        }
        return state;
      }

      if (isDefined(state[tileId])) {
        console.warn(`A Forward Accumulator product already exists for tileId ${tileId}. Overwriting it.`);
      }

      state[tileId] = initForwardAccumulator(undefined, currencyPair);
      return state;
    });

    builder.addCase(addTile, (state, { payload }) => {
      const {
        tile: { tileId, productType },
      } = payload;
      if (isDefined(state?.[tileId])) {
        return state;
      }

      if (productType === 'ForwardAccumulator') {
        state[tileId] = initForwardAccumulator();
      }

      return state;
    });

    builder.addCase(removeTile, (state, { payload: tileId }) => {
      if (isNotDefined(state[tileId])) {
        return state;
      }

      delete state[tileId];
    });

    builder.addCase(duplicateTile, (state, action) => {
      const { sourceTileId, newTileId } = action.payload;

      const sourceTile = state[sourceTileId];

      if (isNotDefined(sourceTile)) {
        return state;
      }

      if (isDefined(state[newTileId])) {
        return state;
      }

      return {
        ...state,
        [newTileId]: {
          ...sourceTile,
          properties: {
            ...sourceTile.properties,
            rfs: { status: 'CANCELLED' },
          },
          tradeCapture: {
            status: 'INITIALIZED',
            sessionId: v4(),
            version: -1,
          },
        },
      };
    });

    // ████████╗██████╗  █████╗ ██████╗ ███████╗     ██████╗ █████╗ ██████╗ ████████╗██╗   ██╗██████╗ ███████╗
    // ╚══██╔══╝██╔══██╗██╔══██╗██╔══██╗██╔════╝    ██╔════╝██╔══██╗██╔══██╗╚══██╔══╝██║   ██║██╔══██╗██╔════╝
    //    ██║   ██████╔╝███████║██║  ██║█████╗      ██║     ███████║██████╔╝   ██║   ██║   ██║██████╔╝█████╗
    //    ██║   ██╔══██╗██╔══██║██║  ██║██╔══╝      ██║     ██╔══██║██╔═══╝    ██║   ██║   ██║██╔══██╗██╔══╝
    //    ██║   ██║  ██║██║  ██║██████╔╝███████╗    ╚██████╗██║  ██║██║        ██║   ╚██████╔╝██║  ██║███████╗
    //    ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝╚═════╝ ╚══════╝     ╚═════╝╚═╝  ╚═╝╚═╝        ╚═╝    ╚═════╝ ╚═╝  ╚═╝╚══════╝
    //

    builder.addMatcher(
      forwardAccumulatorApi.endpoints.checkForwardAccumulator.matchPending,
      function onTradeCaptureUpdated(state, action) {
        const { tileId } = action.meta.arg.originalArgs;
        const product = state[tileId];
        if (isNotDefined(product)) {
          return;
        }

        product.tradeCapture.status = 'CHECKING';
      },
    );

    builder.addMatcher(
      forwardAccumulatorApi.endpoints.checkForwardAccumulator.matchRejected,
      function onTradeCaptureError(state, action) {
        const { tileId } = action.meta.arg.originalArgs;

        const product = state[tileId];
        if (isNotDefined(product)) {
          return;
        }
        product.tradeCapture = {
          status: 'ERROR',
          // TODO: this can be a TIMEOUT, but this can also be another type of error
          error: 'TIMEOUT',
          // TODO: are those fields relevant?
          sessionId: product.tradeCapture.sessionId,
          version: product.tradeCapture.version,
        };
      },
    );

    builder.addMatcher(
      forwardAccumulatorApi.endpoints.checkForwardAccumulator.matchFulfilled,
      function onTradeCaptureUpdated(state, action) {
        const { tileId } = action.meta.arg.originalArgs;

        const product = state[tileId];

        if (isNotDefined(product) || isDefined(product.execution)) {
          // already executed
          return;
        }

        const tcResponse = action.payload;
        product.tradeCapture = tcResponse.tradeCapture;

        if (tcResponse.type === 'error') {
          return;
        }

        // clear tiering to let esp listeners update it
        // TODO FSA search why and check whether we have to do that
        if (product.properties.currencyPair !== tcResponse.properties.currencyPair) {
          product.properties.tiering = undefined;
          product.properties.isEspDefaultTiering = undefined;
        }

        product.properties = {
          ...product.properties,
          ...tcResponse.properties,
        };

        // TODO FSA remove isPriceable if really not needed
        product.properties.isPriceable = tcResponse.tradeCapture.isReadyToPrice;

        if (isDefined(tcResponse.properties.schedule)) {
          product.properties.scheduleIds = Object.keys(tcResponse.properties.schedule);
        }
      },
    );

    // ██████╗ ███████╗███████╗
    // ██╔══██╗██╔════╝██╔════╝
    // ██████╔╝█████╗  ███████╗
    // ██╔══██╗██╔══╝  ╚════██║
    // ██║  ██║██║     ███████║
    // ╚═╝  ╚═╝╚═╝     ╚══════╝
    //

    builder.addMatcher(
      forwardAccumulatorApi.endpoints.requestForwardAccumulatorRfs.matchPending,
      function beforeRfsRequest(state, action) {
        const tileId = action.meta.arg.originalArgs.tileId;
        withForwardAccumulator(tileId, state, (accumulator) => {
          const rfsId = action.meta.arg.originalArgs.rfsId;
          accumulator.properties.rfs = {
            status: 'AWAITING',
            rfsId,
            quoteType: action.meta.arg.originalArgs.body.forceTradable ? 'Tradeable' : 'Indicative',
          };
          accumulator.properties.mifid = { records: [] };
        });
      },
    );

    // TIMESTAMP
    builder.addMatcher(mifidApi.endpoints.recordPrice.matchFulfilled, (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      withForwardAccumulator(tileId, state, (accumulator) => {
        if (isNotDefined(accumulator.properties.mifid)) {
          return;
        }

        accumulator.properties.mifid.records.push({ date: formatISO(action.meta.arg.originalArgs.date) });
      });
    });
    builder.addMatcher(mifidApi.endpoints.recordPrice.matchRejected, (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      withForwardAccumulator(tileId, state, (accumulator) => {
        if (isNotDefined(accumulator.properties.mifid)) {
          return;
        }

        accumulator.properties.mifid.records.push({
          date: formatISO(action.meta.arg.originalArgs.date),
          isError: true,
        });
      });
    });

    //   // REQUEST CANCELLED
    builder.addMatcher(
      forwardAccumulatorApi.endpoints.requestForwardAccumulatorRfs.matchRejected,
      function beforeRfsRequest(state, action) {
        const tileId = action.meta.arg.originalArgs.tileId;
        withForwardAccumulator(tileId, state, (accumulator) => {
          const rfsId = action.meta.arg.originalArgs.rfsId;
          accumulator.properties.rfs = {
            status: 'ERROR',
            rfsId,
            error: {
              multipassErrorCode: '',
              errorCode: 'REQUEST_ERROR',
              errorMessage: 'Request to pricing failed',
            },
          };
        });
      },
    );

    //   // CANCEL
    builder.addMatcher(
      forwardAccumulatorApi.endpoints.cancelForwardAccumulatorRfs.matchPending,
      function beforeCancelRfs(state, action) {
        const { tileId, removeQuote } = action.meta.arg.originalArgs;
        withForwardAccumulator(tileId, state, (accumulator) => {
          if (removeQuote) {
            accumulator.properties.rfs = {
              status: 'CANCELLED',
            };
          } else {
            accumulator.properties.rfs = makeExpired(accumulator.properties.rfs);
          }
        });
      },
    );

    builder.addMatcher(getWorkspaceMatchFulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }

      // clear all products created before workspace has loaded
      for (const id of Object.keys(state)) {
        delete state[id];
      }

      action.payload.products.forwardAccumulator?.forEach((product) => {
        state[product.tileId] = mapToForwardAccumulator(product);
      });
    });

    reduceForwardAccumulatorOnFetchSharedWorkspace(builder);
  },
  selectors: {
    getForwardAccumulator: (state, tileId: string): FxForwardAccumulator | undefined => state[tileId],
    getAllForwardAccumulatorProducts: createForwardAccumulatorSelector([(state) => state], (state) =>
      Object.entries(state).map(([tileId, forwardAccumulator]) => ({ tileId, ...forwardAccumulator })),
    ),
  },
});

function withForwardAccumulator(
  tileId: string,
  state: ForwardAccumulatorById,
  action: (accumulator: Draft<FxForwardAccumulator>) => void,
) {
  const currentAccumulator = state[tileId];
  if (isDefined(currentAccumulator)) {
    action(currentAccumulator);
  }
}

function makeExpired(rfs: ForwardAccumulatorRfs): ForwardAccumulatorRfs {
  if (rfs.status === 'EXPIRED' || rfs.status === 'PRICING') {
    return {
      status: 'EXPIRED',
      quote: rfs.quote,
    };
  }
  return { status: 'CANCELLED' };
}
